(() => {
  'use strict';

  if (customElements.get('redac-configurator')) return;

  const escapeHtml = (value) => String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]
  );

  class RedacConfigurator extends HTMLElement {
    constructor() {
      super();
      this.config = null;
      this.mode = 'IREDAC';
      this.units = [this.createUnit()];
      this.loadingError = null;
      this.priceCache = new Map();
      this.imageCache = new Map();
      this.priceRefreshId = 0;
      this.configurationRevision = 0;
    }

    createUnit() {
      return {
        frontplate: '',
        clusters: [{ M1: '', M2: '' }]
      };
    }

    async connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.innerHTML = `
        <div class="redac-configurator__inner">
          <div class="redac-configurator__status" role="status" aria-live="polite">
            Loading REDAC configuration …
          </div>
        </div>`;

      try {
        const url = this.dataset.configUrl;
        if (!url) throw new Error('The configuration file URL is missing.');

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Could not load configuration file (HTTP ${response.status}).`);
        }

        this.config = await response.json();
        const defaultSystem = String(this.dataset.defaultSystem || 'iREDAC')
          .trim()
          .toLowerCase();
        this.mode = defaultSystem === 'mredac'
          ? 'SINGLE_USE'
          : 'IREDAC';

        if (!this.config.articles || !this.config.bom) {
          throw new Error('The configuration file contains no product or BOM data.');
        }

        this.render();
      } catch (error) {
        this.loadingError = error;
        this.innerHTML = `
          <div class="redac-configurator__inner">
            <p role="alert">
              Could not load the REDAC configuration:
              ${escapeHtml(error.message)}
            </p>
          </div>`;
        console.error('[REDAC-Konfigurator]', error);
      }
    }

    articleLabel(sku) {
      const article = this.config?.articles?.[sku];
      if (!article) return sku;

      return article.name ||
        article.shopify?.productTitle ||
        article.title ||
        article.label ||
        article.description ||
        sku;
    }

    getFrontplateOptions() {
      const bom = this.config.bom['REDAC-MREDAC'];
      const rule = (bom?.rules || []).find(
        (item) => item.type === 'exactlyOne'
      );

      return rule?.targets || [];
    }

    getMathModuleOptions(position) {
      const bom = this.config.bom['REDAC-CLUSTER'];
      const rule = (bom?.rules || []).find(
        (item) => item.type === 'selection' && item.position === position
      );

      return rule?.options || [];
    }

    optionList(options, selected, placeholder) {
      return [
        `<option value="">${escapeHtml(placeholder)}</option>`,
        ...options.map((sku) => `
          <option value="${escapeHtml(sku)}"
            ${sku === selected ? 'selected' : ''}>
            ${escapeHtml(this.articleLabel(sku))} (${escapeHtml(sku)})
          </option>`)
      ].join('');
    }

    render() {
      const frontplates = this.getFrontplateOptions();
      const m1Options = this.getMathModuleOptions('M1');
      const m2Options = this.getMathModuleOptions('M2');

      this.innerHTML = `
        <div class="redac-configurator__inner">
          <header class="redac-configurator__header">
            <div>
              <h2>REDAC-CONFIGURATOR</h2>
              <p>
                Configuration
                ${escapeHtml(this.config.configVersion || 'unknown')}
              </p>
            </div>
          </header>

          <section class="redac-configurator__setup">
            <h3>Product variant</h3>

            <label>
              <input type="radio" name="redac-mode" value="IREDAC"
                ${this.mode === 'IREDAC' ? 'checked' : ''}>
              iREDAC – integrated mREDAC assemblies
            </label>

            <label>
              <input type="radio" name="redac-mode" value="SINGLE_USE"
                ${this.mode === 'SINGLE_USE' ? 'checked' : ''}>
              mREDAC – standalone assembly
            </label>

            <div class="redac-configurator__integrated"
              ${this.mode !== 'IREDAC' ? 'hidden' : ''}>
              <label for="redac-unit-count">
                Number of mREDAC assemblies (1–7)
              </label>
              <input id="redac-unit-count" type="number"
                min="1" max="7" step="1"
                value="${this.units.length}">
              <p>
                Each assembly has its own front panel,
                cluster count and math module selection.
              </p>
            </div>

            <div class="redac-configurator__standalone"
              ${this.mode !== 'SINGLE_USE' ? 'hidden' : ''}>
              <p role="alert">
                The standalone mREDAC assembly still requires
                enclosure and connection components to be defined.
              </p>
            </div>
          </section>

          <section class="redac-configurator__units">
            <h3>mREDAC-Configuration</h3>
            <div class="redac-configurator__unit-list">
              ${this.renderUnits(frontplates, m1Options, m2Options)}
            </div>
          </section>

          <section class="redac-configurator__bom">
            <h3>Generated bill of materials</h3>
            <p>
              Identical products are consolidated by quantity.
              Shopify gross prices are loaded dynamically and converted to net prices
              using the configured standard VAT rate.
            </p>
            <div class="redac-configurator__bom-content"></div>
          </section>

          <section class="redac-configurator__validation"
            role="status" aria-live="polite"></section>

          <p class="redac-configurator__notice" role="note">
            Net prices plus VAT – default VAT rate 19%, adjustable in the configuration.
            Preliminary configuration – not approved for ordering.
            The outer iREDAC bill of materials is still incomplete.
          </p>
        </div>`;

      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.updateBillOfMaterials();
    }

    renderUnits(frontplates, m1Options, m2Options) {
      const visibleUnits = this.mode === 'SINGLE_USE'
        ? this.units.slice(0, 1)
        : this.units;

      return visibleUnits.map((unit, unitIndex) => `
        <section class="redac-configurator__unit"
          data-unit-index="${unitIndex}">
          <h4>
            ${this.mode === 'SINGLE_USE'
              ? 'Standalone mREDAC assembly'
              : `mREDAC assembly ${unitIndex + 1}`}
          </h4>

          <label for="redac-frontplate-${unitIndex}">
            Front panel variant
          </label>
          <select id="redac-frontplate-${unitIndex}"
            data-frontplate="${unitIndex}">
            ${this.optionList(
              frontplates,
              unit.frontplate,
              'Please select a front panel'
            )}
          </select>

          <label for="redac-cluster-count-${unitIndex}">
            Number of clusters (1–3)
          </label>
          <select id="redac-cluster-count-${unitIndex}"
            data-cluster-count="${unitIndex}">
            ${[1, 2, 3].map((count) => `
              <option value="${count}"
                ${unit.clusters.length === count ? 'selected' : ''}>
                ${count}
              </option>`).join('')}
          </select>

          <div class="redac-configurator__cluster-list">
            ${unit.clusters.map((cluster, clusterIndex) => `
              <fieldset class="redac-configurator__cluster">
                <legend>Cluster ${clusterIndex + 1}</legend>

                <label for="redac-${unitIndex}-${clusterIndex}-m1">
                  Math module M1
                </label>
                <select id="redac-${unitIndex}-${clusterIndex}-m1"
                  data-unit="${unitIndex}"
                  data-cluster="${clusterIndex}"
                  data-slot="M1">
                  ${this.optionList(
                    m1Options, cluster.M1, 'Please select M1'
                  )}
                </select>

                <label for="redac-${unitIndex}-${clusterIndex}-m2">
                  Math module M2
                </label>
                <select id="redac-${unitIndex}-${clusterIndex}-m2"
                  data-unit="${unitIndex}"
                  data-cluster="${clusterIndex}"
                  data-slot="M2">
                  ${this.optionList(
                    m2Options, cluster.M2, 'Please select M2'
                  )}
                </select>
              </fieldset>
            `).join('')}
          </div>
        </section>
      `).join('');
    }

    handleChange = (event) => {
      const target = event.target;

      if (target.matches('input[name="redac-mode"]')) {
        this.mode = target.value;
        this.render();
        return;
      }

      if (target.matches('[data-frontplate]')) {
        const index = Number(target.dataset.frontplate);
        if (this.units[index]) {
          this.units[index].frontplate = target.value;
        }
        this.updateBillOfMaterials();
        return;
      }

      if (target.matches('[data-cluster-count]')) {
        const index = Number(target.dataset.clusterCount);
        const count = Number(target.value);

        if (!this.units[index] || !Number.isInteger(count) ||
            count < 1 || count > 3) {
          this.updateBillOfMaterials();
          return;
        }

        const clusters = this.units[index].clusters;

        while (clusters.length < count) {
          clusters.push({ M1: '', M2: '' });
        }

        clusters.length = count;
        this.render();
        return;
      }

      if (target.matches('select[data-slot]')) {
        const unitIndex = Number(target.dataset.unit);
        const clusterIndex = Number(target.dataset.cluster);
        const slot = target.dataset.slot;

        if (this.units[unitIndex]?.clusters[clusterIndex] &&
            (slot === 'M1' || slot === 'M2')) {
          this.units[unitIndex].clusters[clusterIndex][slot] = target.value;
        }

        this.updateBillOfMaterials();
      }
    };

    handleInput = (event) => {
      const target = event.target;
      if (!target.matches('#redac-unit-count')) return;

      const count = Number(target.value);
      if (!Number.isInteger(count) || count < 1 || count > 7) {
        this.updateBillOfMaterials();
        return;
      }

      while (this.units.length < count) {
        this.units.push(this.createUnit());
      }

      this.units.length = count;
      this.render();
    };

    getActiveUnits() {
      return this.mode === 'SINGLE_USE'
        ? this.units.slice(0, 1)
        : this.units;
    }

    calculateBom() {
      const quantities = new Map();
      const unitQuantities = [];
      let currentUnitQuantities = null;
      const errors = [];
      const activeUnits = this.getActiveUnits();
      const mredacBom = this.config.bom['REDAC-MREDAC'];
      const carrierBom = this.config.bom['REDAC-MREDAC-CARRIER'];
      const clusterBom = this.config.bom['REDAC-CLUSTER'];

      const add = (sku, quantity = 1) => {
        if (!sku || !Number.isFinite(quantity) || quantity <= 0) {
          errors.push(`Invalid BOM entry: ${sku || '(leer)'}`);
          return;
        }

        quantities.set(sku, (quantities.get(sku) || 0) + quantity);
        if (currentUnitQuantities) {
          currentUnitQuantities.set(
            sku,
            (currentUnitQuantities.get(sku) || 0) + quantity
          );
        }
      };

      if (!mredacBom || !carrierBom || !clusterBom) {
        return {
          quantities,
          errors: ['Required BOM data is missing.'],
          complete: false,
          activeUnits
        };
      }

      const frontplates = this.getFrontplateOptions();
      const m1Options = this.getMathModuleOptions('M1');
      const m2Options = this.getMathModuleOptions('M2');

      for (const [unitIndex, unit] of activeUnits.entries()) {
        currentUnitQuantities = new Map();
        const unitLabel = `mREDAC ${unitIndex + 1}`;

        if (!frontplates.includes(unit.frontplate)) {
          errors.push(`${unitLabel}: Please select a front panel.`);
        }

        // Gemeinsame Ausstattung genau einmal je mREDAC.
        for (const component of mredacBom.components || []) {
          if (component.position === 'carrier') {
            const quantity = typeof component.quantity === 'number'
              ? component.quantity
              : 1;

            add(component.sku, quantity);

            // CTRL and T are taken from the carrier BOM.
            for (const child of carrierBom.components || []) {
              if (typeof child.quantity !== 'number') {
                errors.push(`Unknown quantity in carrier BOM: ${child.sku}`);
                continue;
              }
              add(child.sku, child.quantity * quantity);
            }
          }
        }

        if (frontplates.includes(unit.frontplate)) {
          add(unit.frontplate, 1);
        }

        for (const [clusterIndex, cluster] of unit.clusters.entries()) {
          const clusterLabel = `${unitLabel}, Cluster ${clusterIndex + 1}`;

          if (!m1Options.includes(cluster.M1)) {
            errors.push(`${clusterLabel}: Please select math module M1.`);
          }

          if (!m2Options.includes(cluster.M2)) {
            errors.push(`${clusterLabel}: Please select math module M2.`);
          }

          for (const component of clusterBom.components || []) {
            if (component.sku === 'M_SLOT_1' ||
                component.sku === 'M_SLOT_2') {
              const position = component.position;
              const selectedSku = position === 'M1'
                ? cluster.M1
                : position === 'M2'
                  ? cluster.M2
                  : '';

              const allowed = position === 'M1'
                ? m1Options
                : m2Options;

              if (allowed.includes(selectedSku)) {
                add(selectedSku, component.quantity);
              }
            } else {
              // Required U, C, I and SH modules for each cluster.
              add(component.sku, component.quantity);
            }
          }
        }

        unitQuantities.push(currentUnitQuantities);
        currentUnitQuantities = null;
      }

      if (this.mode === 'SINGLE_USE') {
        // Show unresolved items, but do not output them as actual products.
        errors.push(
          'Standalone: enclosure and connection components are still pending.'
        );
      }

      if (this.mode === 'IREDAC') {
        errors.push(
          'iREDAC: outer enclosure, frame and backplane components are still missing.'
        );
      }

      const selectionsComplete = activeUnits.every((unit) =>
        frontplates.includes(unit.frontplate) &&
        unit.clusters.length >= 1 &&
        unit.clusters.length <= 3 &&
        unit.clusters.every((cluster) =>
          m1Options.includes(cluster.M1) &&
          m2Options.includes(cluster.M2)
        )
      );

      return {
        quantities,
        unitQuantities,
        errors,
        selectionsComplete,
        activeUnits
      };
    }

    getVatRate() {
      const rate = Number(this.config?.tax?.defaultVatRate ?? 19);
      return Number.isFinite(rate) && rate >= 0 && rate <= 100
        ? rate
        : 19;
    }

    formatMoney(value) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: this.config?.currency || 'EUR'
      }).format(value);
    }

    async getShopifyImage(sku) {
      if (this.imageCache.has(sku)) return this.imageCache.get(sku);
      const promise = (async () => {
        const mapping = this.config?.articles?.[sku]?.shopify;
        if (!mapping?.handle) return null;
        try {
          const response = await fetch(`/products/${encodeURIComponent(mapping.handle)}.js`);
          if (!response.ok) return null;
          const product = await response.json();
          const image = product.featured_image || product.images?.[0] || null;
          return typeof image === 'string' && image.trim() ? image : null;
        } catch (error) {
          console.warn('[REDAC-Konfigurator] Product image unavailable:', sku, error);
          return null;
        }
      })();
      this.imageCache.set(sku, promise);
      return promise;
    }

    async updateProductImages(result, output) {
      await Promise.all([...result.quantities.keys()].map(async (sku) => {
        const image = await this.getShopifyImage(sku);
        if (!output.isConnected) return;
        const imageElement = output.querySelector(`[data-product-image="${sku}"]`);
        const fallback = output.querySelector(`[data-product-image-fallback="${sku}"]`);
        if (!imageElement || !fallback) return;
        if (image) {
          imageElement.src = image;
          imageElement.hidden = false;
          fallback.hidden = true;
        } else {
          imageElement.hidden = true;
          fallback.hidden = false;
        }
      }));
    }

    async getShopifyPrice(sku) {
      if (this.priceCache.has(sku)) return this.priceCache.get(sku);

      const promise = (async () => {
        const article = this.config?.articles?.[sku];
        const mapping = article?.shopify;
        if (!mapping?.handle || !mapping?.variantId) {
          return { status: 'missing_mapping', sku };
        }

        try {
          const response = await fetch(
            `/products/${encodeURIComponent(mapping.handle)}.js`
          );
          if (!response.ok) {
            return { status: 'http_error', httpStatus: response.status, sku };
          }

          const product = await response.json();
          const variant = (product.variants || []).find(
            item => String(item.id) === String(mapping.variantId)
          );
          if (!variant) return { status: 'missing_variant', sku };

          const actualSku = String(variant.sku || '').trim();
          if (actualSku.toLowerCase() !== sku.toLowerCase()) {
            return { status: 'sku_mismatch', sku, actualSku };
          }

          const gross = Number(variant.price) / 100;
          if (!Number.isFinite(gross) || gross < 0) {
            return { status: 'invalid_price', sku };
          }

          const vatRate = this.getVatRate();
          const net = gross / (1 + vatRate / 100);
          return {
            status: 'ok',
            sku,
            gross,
            net,
            vatRate,
            available: Boolean(variant.available)
          };
        } catch (error) {
          return { status: 'fetch_error', sku, message: error.message };
        }
      })();

      this.priceCache.set(sku, promise);
      return promise;
    }

    async updatePriceDisplay(result, output, revision) {
      const refreshId = ++this.priceRefreshId;
      const summary = output.querySelector('.redac-configurator__price-summary');
      if (!summary) return;

      const skus = [...result.quantities.keys()];
      // REDAC-CLUSTER is a parent assembly whose BOM is expanded into its modules.
      // Its catalog price is therefore not added on top of those module prices.
      const pricedSkus = skus.filter(sku => sku !== 'REDAC-CLUSTER');
      const priceEntries = await Promise.all(
        pricedSkus.map(async sku => [sku, await this.getShopifyPrice(sku)])
      );

      if (refreshId !== this.priceRefreshId || !output.isConnected) return;
      const prices = new Map(priceEntries);

      for (const sku of skus) {
        const price = prices.get(sku);
        const priceCell = output.querySelector(
          `[data-price-sku="${sku}"]`
        );
        const availabilityCell = output.querySelector(
          `[data-availability-sku="${sku}"]`
        );
        if (!priceCell || !availabilityCell) continue;

        if (sku === 'REDAC-CLUSTER') {
          priceCell.textContent = 'Not added (BOM expanded)';
          availabilityCell.textContent = 'Assembly';
          continue;
        }

        if (price.status !== 'ok') {
          priceCell.textContent = 'Price missing';
          availabilityCell.textContent = this.priceErrorLabel(price);
          continue;
        }

        priceCell.textContent = `Net: ${this.formatMoney(price.net)} | Gross: ${this.formatMoney(price.gross)}`;
        availabilityCell.textContent = price.available
          ? 'Available'
          : 'Currently unavailable';
      }

      // Calculate net subtotals for the BOM groups rendered in the table.
      const groupSkus = {
        cluster: sku => sku.startsWith('REDAC-MOD-'),
        'mredac-base': sku => [
          'REDAC-MREDAC-CARRIER',
          'REDAC-MREDAC-CTRL',
          'REDAC-MREDAC-T'
        ].includes(sku),
        'mredac-components': sku => [
          'REDAC-MREDAC-FP',
          'REDAC-MREDAC-CFP'
        ].includes(sku),
        'iredac-components': sku => sku.startsWith('REDAC-IREDAC-'),
        assemblies: sku => [
          'REDAC-CLUSTER',
          'REDAC-IREDAC',
          'REDAC-MREDAC'
        ].includes(sku),
        other: sku => !sku.startsWith('REDAC-MOD-')
          && ![
            'REDAC-MREDAC-CARRIER',
            'REDAC-MREDAC-CTRL',
            'REDAC-MREDAC-T',
            'REDAC-MREDAC-FP',
            'REDAC-MREDAC-CFP',
            'REDAC-CLUSTER',
            'REDAC-IREDAC',
            'REDAC-MREDAC'
          ].includes(sku)
          && !sku.startsWith('REDAC-IREDAC-')
      };

      const roundMoney = value =>
        Math.round((value + Number.EPSILON) * 100) / 100;

      for (const [groupId, matchesSku] of Object.entries(groupSkus)) {
        const cell = output.querySelector(
          `[data-group-total="${groupId}"]`
        );
        if (!cell) continue;

        const entries = [...result.quantities.entries()]
          .filter(([sku]) => matchesSku(sku));

        if (!entries.length) {
          cell.textContent = '—';
          continue;
        }

        if (!result.selectionsComplete) {
          cell.textContent = 'Configuration incomplete';
          continue;
        }

        let subtotalNet = 0;
        let subtotalGross = 0;
        let complete = true;

        for (const [sku, quantity] of entries) {
          // Parent assembly prices are not added to their expanded BOM.
          if (sku === 'REDAC-CLUSTER') continue;

          const price = prices.get(sku);
          if (!price || price.status !== 'ok') {
            complete = false;
            break;
          }

          subtotalNet += price.net * quantity;
          subtotalGross += price.gross * quantity;
        }

        cell.textContent = complete
          ? `Net: ${this.formatMoney(roundMoney(subtotalNet))} | Gross: ${this.formatMoney(roundMoney(subtotalGross))}`
          : 'Cannot calculate – component price missing';
      }

      const rate = this.getVatRate();
      const totalNet = (map) => {
        if (!result.selectionsComplete) return null;
        let total = 0;
        for (const [sku, quantity] of map.entries()) {
          if (sku === 'REDAC-CLUSTER') continue;
          const price = prices.get(sku);
          if (!price || price.status !== 'ok') return null;
          total += price.net * quantity;
        }
        return Math.round((total + Number.EPSILON) * 100) / 100;
      };

      const unitTotals = (result.unitQuantities || []).map((quantities, index) => ({
        label: this.mode === 'SINGLE_USE'
          ? 'Standalone mREDAC assembly'
          : `mREDAC ${index + 1}`,
        total: totalNet(quantities)
      }));

      const unresolved = priceEntries.filter(([, price]) => price.status !== 'ok');
      const unavailable = priceEntries.filter(([, price]) =>
        price.status === 'ok' && !price.available
      );
      const unitHtml = unitTotals.map(unit => `
        <p><strong>${escapeHtml(unit.label)} – Net:</strong>
          ${unit.total === null
            ? 'Cannot calculate – at least one component price is missing.'
            : escapeHtml(this.formatMoney(unit.total))}
        </p>`).join('');

      const combinedNet = totalNet(result.quantities);
      const completenessNote = this.mode === 'IREDAC'
        ? 'iREDAC subtotal for the configured mREDAC assemblies. This is not the full iREDAC price: outer enclosure, frame and backplane components have not yet been fully defined.'
        : 'mREDAC subtotal. This is not the final price: enclosure and connection components have not yet been fully defined.';

      const combinedGross = result.selectionsComplete
        && [...result.quantities.keys()]
          .filter(sku => sku !== 'REDAC-CLUSTER')
          .every(sku => prices.get(sku)?.status === 'ok')
        ? [...result.quantities.entries()].reduce((sum, [sku, quantity]) => {
            if (sku === 'REDAC-CLUSTER') return sum;
            return sum + prices.get(sku).gross * quantity;
          }, 0)
        : null;

      const roundedNet = combinedNet === null
        ? null
        : Math.round((combinedNet + Number.EPSILON) * 100) / 100;
      const roundedGross = combinedGross === null
        ? null
        : Math.round((combinedGross + Number.EPSILON) * 100) / 100;
      const vatAmount = roundedNet === null || roundedGross === null
        ? null
        : Math.round((roundedGross - roundedNet + Number.EPSILON) * 100) / 100;

      summary.innerHTML = `
        <h4>Price summary</h4>
        <p>Shopify gross prices; net calculated using ${rate}% VAT.
          <strong>The tax rate can be adjusted in config/redac-config.json.</strong>
        </p>
        ${unitHtml}
        <p><strong>Subtotal of listed components – Net:</strong>
          ${roundedNet === null
            ? 'Cannot calculate – at least one component price is missing.'
            : escapeHtml(this.formatMoney(roundedNet))}
        </p>
        <p><strong>VAT (${rate}%):</strong>
          ${vatAmount === null
            ? 'Cannot calculate'
            : escapeHtml(this.formatMoney(vatAmount))}
        </p>
        <p><strong>Subtotal of listed components – Gross:</strong>
          ${roundedGross === null
            ? 'Cannot calculate – at least one component price is missing.'
            : escapeHtml(this.formatMoney(roundedGross))}
        </p>
        <p role="note">${escapeHtml(completenessNote)}</p>
        ${!result.selectionsComplete
          ? '<p role="status">Please select the front panels and both math modules for all clusters to calculate complete subtotals.</p>'
          : ''}
        ${unresolved.length ? `<p role="alert">Missing price/mapping data: ${
          unresolved.map(([sku, price]) => `${escapeHtml(sku)} (${escapeHtml(this.priceErrorLabel(price))})`).join(', ')
        }</p>` : '<p>All required component prices have been retrieved successfully.</p>'}
        ${unavailable.length ? `<p role="alert">Currently unavailable: ${
          unavailable.map(([sku]) => escapeHtml(sku)).join(', ')
        }</p>` : ''}
      `;
            // Provide the price summary for the REDAC quote request.
      const emailPriceItems = skus
        .filter(sku => sku !== 'REDAC-CLUSTER')
        .map(sku => {
          const quantity = result.quantities.get(sku);
          const price = prices.get(sku);

          if (!price || price.status !== 'ok') {
            return {
              sku,
              quantity,
              status: 'missing',
              message: price
                ? this.priceErrorLabel(price)
                : 'Price unavailable'
            };
          }

          const lineNet = Math.round(
            (price.net * quantity + Number.EPSILON) * 100
          ) / 100;

          const lineGross = Math.round(
            (price.gross * quantity + Number.EPSILON) * 100
          ) / 100;

          return {
            sku,
            quantity,
            status: 'ok',
            unitNet: price.net,
            unitGross: price.gross,
            lineNet,
            lineGross,
            available: price.available
          };
        });

      this.dispatchEvent(new CustomEvent('redac:prices-updated', {
        bubbles: true,
        detail: {
          revision,
          selectionsComplete: Boolean(result.selectionsComplete),
          vatRate: rate,
          items: emailPriceItems,
          totalNet: roundedNet,
          totalVat: vatAmount,
          totalGross: roundedGross,
          unresolved: unresolved.map(([sku, price]) => ({
            sku,
            message: this.priceErrorLabel(price)
          })),
          unavailable: unavailable.map(([sku]) => sku),
          completenessNote
        }
      }));
    }

    priceErrorLabel(price) {
      const labels = {
        missing_mapping: 'Shopify mapping missing',
        http_error: `Shopify returned HTTP ${price.httpStatus}`,
        missing_variant: 'Variant not found',
        sku_mismatch: `SKU mismatch (${price.actualSku || 'leer'})`,
        invalid_price: 'Invalid price',
        fetch_error: 'Retrieval failed'
      };
      return labels[price.status] || 'Unknown pricing error';
    }

    updateBillOfMaterials() {
      if (!this.config) return;

      const result = this.calculateBom();
      const output = this.querySelector('.redac-configurator__bom-content');
      const validation = this.querySelector('.redac-configurator__validation');

      if (!output || !validation) return;

      const bomGroups = [
        { id: 'cluster', label: 'Compute cluster' },
        { id: 'mredac-base', label: 'mREDAC base components' },
        { id: 'mredac-components', label: 'mREDAC components' },
        { id: 'iredac-components', label: 'iREDAC components' },
        { id: 'assemblies', label: 'Assemblyn-Katalog' },
        { id: 'other', label: 'Additional components' }
      ];

      const groupForSku = (sku) => {
        if (sku === 'REDAC-CLUSTER' ||
            sku === 'REDAC-IREDAC' ||
            sku === 'REDAC-MREDAC') {
          return 'assemblies';
        }

        if (sku.startsWith('REDAC-MOD-')) return 'cluster';

        if ([
          'REDAC-MREDAC-CARRIER',
          'REDAC-MREDAC-CTRL',
          'REDAC-MREDAC-T'
        ].includes(sku)) return 'mredac-base';

        if ([
          'REDAC-MREDAC-FP',
          'REDAC-MREDAC-CFP'
        ].includes(sku)) return 'mredac-components';

        if (sku.startsWith('REDAC-IREDAC-')) return 'iredac-components';

        return 'other';
      };

      const groupedRows = new Map(
        bomGroups.map(group => [group.id, []])
      );

      for (const [sku, quantity] of result.quantities.entries()) {
        groupedRows.get(groupForSku(sku)).push([sku, quantity]);
      }

      const rows = bomGroups.map(group => {
        const entries = groupedRows.get(group.id);
        if (!entries.length) return '';

        if (group.id === 'cluster') {
          const clusterOrder = [
            'REDAC-MOD-U',
            'REDAC-MOD-C',
            'REDAC-MOD-I',
            'REDAC-MOD-SH',
            'REDAC-MOD-INT',
            'REDAC-MOD-MDR',
            'REDAC-MOD-MATRIX',
            'REDAC-MOD-CONVDELAY',
            'REDAC-MOD-COMM',
            'REDAC-MOD-COMP',
            'REDAC-MOD-FUNA',
            'REDAC-MOD-FUND'
          ];
          const rank = new Map(
            clusterOrder.map((sku, index) => [sku, index])
          );

          entries.sort(([a], [b]) =>
            (rank.get(a) ?? Number.MAX_SAFE_INTEGER)
              - (rank.get(b) ?? Number.MAX_SAFE_INTEGER)
            || a.localeCompare(b)
          );
        } else {
          entries.sort(([a], [b]) => a.localeCompare(b));
        }

        const itemRows = entries.map(([sku, quantity]) => {
          const exists = Boolean(this.config.articles[sku]);
          return `
            <tr>
              <td style="display:flex;align-items:center;gap:10px;min-width:170px">
                <img data-product-image="${escapeHtml(sku)}"
                  alt="${escapeHtml(this.articleLabel(sku))}"
                  loading="lazy" width="56" height="56" hidden
                  style="flex:0 0 56px;width:56px;height:56px;object-fit:contain;border:1px solid #dce4ef;border-radius:6px;background:#f7f9fc">
                <span data-product-image-fallback="${escapeHtml(sku)}" aria-hidden="true"
                  style="display:inline-flex;flex:0 0 56px;align-items:center;justify-content:center;width:56px;height:56px;border:1px solid #dce4ef;border-radius:6px;background:#f7f9fc;color:#61708a;font-size:20px">—</span>
                <span>${escapeHtml(this.articleLabel(sku))}</span>
              </td>
              <td><code>${escapeHtml(sku)}</code></td>
              <td>${quantity}</td>
              <td data-price-sku="${escapeHtml(sku)}">Loading price …</td>
              <td data-availability-sku="${escapeHtml(sku)}">Checking …</td>
              <td>${exists
                ? 'In configuration catalog'
                : 'Check product mapping'}</td>
            </tr>`;
        }).join('');

        return `
          <tr class="redac-configurator__bom-group">
            <th colspan="6" scope="colgroup">${escapeHtml(group.label)}</th>
          </tr>
          ${itemRows}
          <tr class="redac-configurator__bom-subtotal">
            <td colspan="3"><strong>Subtotal – ${escapeHtml(group.label)}</strong></td>
            <td colspan="3" data-group-total="${escapeHtml(group.id)}">
              Calculating …
            </td>
          </tr>`;
      }).join('');

      output.innerHTML = `
        ${this.mode === 'SINGLE_USE' ? `
          <p role="alert">
            Pending items: enclosure and connection components are
            not yet defined and therefore not included in the item quantity.
          </p>` : `
          <p role="alert">
            The outer iREDAC bill of materials, including enclosure, frame
            and backplane, is still incomplete.
          </p>`}

        ${rows ? `
          <table class="redac-configurator__bom-table">
            <thead>
              <tr>
                <th>Product</th><th>SKU</th><th>Quantity</th>
                <th>Net per unit</th><th>Availability</th><th>Mapping</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>` : '<p>No product positions have been determined yet.</p>'}
        <div class="redac-configurator__price-summary" aria-live="polite">
          Retrieving Shopify prices …
        </div>
      `;

      const revision = ++this.configurationRevision;
      void this.updatePriceDisplay(result, output, revision);
      void this.updateProductImages(result, output);

      const totalClusters = result.activeUnits.reduce(
        (sum, unit) => sum + unit.clusters.length, 0
      );

      if (!result.selectionsComplete) {
        validation.textContent =
          'Please select all front panels and math modules. ' +
          'The displayed bill of materials is provisional until then.';
      } else {
        validation.textContent =
          `Selection complete: ${result.activeUnits.length} mREDAC assembly/assemblies, ` +
          `${totalClusters} clusters in total. Not approved for ordering.`;
      }

      this.dispatchEvent(new CustomEvent('redac:configuration-changed', {
        bubbles: true,
        detail: {
          revision,
          mode: this.mode,
          units: result.activeUnits.map((unit) => ({
            frontplate: unit.frontplate,
            clusters: unit.clusters.map((cluster) => ({ ...cluster }))
          })),
          selectionsComplete: Boolean(result.selectionsComplete),
          billOfMaterials: Object.fromEntries(result.quantities),
          warnings: result.errors
        }
      }));
    }
  }

  customElements.define('redac-configurator', RedacConfigurator);
})();